import { useState } from 'react';
import { generateAndDownloadVIPToken } from '../../services/vipTokenGenerator';
import LoadingSpinner from '../common/LoadingSpinner';

const TokenGeneratorModal = ({ user, onClose, adminEmail }) => {
  const [generating, setGenerating] = useState(false);
  const [progress, setProgress] = useState('');
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  const handleGenerate = async () => {
    setGenerating(true);
    setError(null);
    setProgress('Initializing...');

    try {
      setProgress('Loading user data...');
      await new Promise(resolve => setTimeout(resolve, 500));

      setProgress('Generating certificate with QR code...');
      const result = await generateAndDownloadVIPToken(user, adminEmail, {
        uploadToStorage: true,
        saveRecord: true
      });

      if (result.success) {
        setProgress('Token generated successfully!');
        setSuccess(true);
        setTimeout(() => {
          onClose();
        }, 2000);
      } else {
        setError(result.message || 'Failed to generate token');
      }
    } catch (err) {
      console.error('Token generation error:', err);
      setError('An unexpected error occurred. Please try again.');
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '600px' }}>
        <div className="modal-header">
          <h2>Generate VIP Token Certificate</h2>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>

        <div className="modal-body">
          {!generating && !success && (
            <>
              <div style={{ marginBottom: '1.5rem' }}>
                <h3 style={{ marginBottom: '1rem', color: '#1e293b' }}>User Details</h3>
                <div style={{ 
                  background: '#f8fafc', 
                  padding: '1rem', 
                  borderRadius: '8px',
                  border: '1px solid #e2e8f0'
                }}>
                  <div style={{ display: 'grid', gap: '0.75rem' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                      {user.profileImage && (
                        <img 
                          src={user.profileImage} 
                          alt={user.name}
                          style={{
                            width: '60px',
                            height: '60px',
                            borderRadius: '50%',
                            objectFit: 'cover',
                            border: '3px solid #14b8a6'
                          }}
                        />
                      )}
                      <div>
                        <p style={{ fontWeight: '600', fontSize: '1.125rem', color: '#1e293b' }}>
                          {user.name}
                        </p>
                        <p style={{ fontSize: '0.875rem', color: '#64748b' }}>
                          VIP ID: {user.id}
                        </p>
                      </div>
                    </div>
                    <div style={{ paddingTop: '0.75rem', borderTop: '1px solid #e2e8f0' }}>
                      <p style={{ fontSize: '0.875rem', color: '#64748b', marginBottom: '0.25rem' }}>
                        <strong>Email:</strong> {user.email}
                      </p>
                      <p style={{ fontSize: '0.875rem', color: '#64748b', marginBottom: '0.25rem' }}>
                        <strong>Phone:</strong> {user.phone}
                      </p>
                      <p style={{ fontSize: '0.875rem', color: '#64748b' }}>
                        <strong>Address:</strong> {user.address}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div style={{ 
                background: '#eff6ff', 
                padding: '1rem', 
                borderRadius: '8px',
                marginBottom: '1.5rem',
                border: '1px solid #bfdbfe'
              }}>
                <h4 style={{ color: '#1e40af', marginBottom: '0.5rem', fontSize: '0.875rem' }}>
                  📋 What will be generated:
                </h4>
                <ul style={{ 
                  margin: 0, 
                  paddingLeft: '1.5rem',
                  color: '#1e40af',
                  fontSize: '0.875rem'
                }}>
                  <li>High-quality VIP certificate (1200x1600px)</li>
                  <li>User photo with rounded border</li>
                  <li>QR code for verification</li>
                  <li>Complete user details</li>
                  <li>Auto-download as PNG file</li>
                  <li>Saved to Firebase Storage</li>
                </ul>
              </div>

              {error && (
                <div style={{
                  background: '#fee2e2',
                  color: '#991b1b',
                  padding: '1rem',
                  borderRadius: '8px',
                  marginBottom: '1rem',
                  border: '1px solid #fecaca'
                }}>
                  <strong>Error:</strong> {error}
                </div>
              )}
            </>
          )}

          {generating && (
            <div style={{ textAlign: 'center', padding: '2rem 0' }}>
              <LoadingSpinner />
              <p style={{ 
                marginTop: '1rem', 
                color: '#14b8a6',
                fontWeight: '600',
                fontSize: '1.125rem'
              }}>
                {progress}
              </p>
              <p style={{ 
                marginTop: '0.5rem', 
                color: '#64748b',
                fontSize: '0.875rem'
              }}>
                Please wait, this may take a few seconds...
              </p>
            </div>
          )}

          {success && (
            <div style={{ textAlign: 'center', padding: '2rem 0' }}>
              <div style={{
                width: '80px',
                height: '80px',
                background: '#dcfce7',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 1rem'
              }}>
                <span style={{ fontSize: '3rem' }}>✓</span>
              </div>
              <h3 style={{ color: '#16a34a', marginBottom: '0.5rem' }}>
                Token Generated Successfully!
              </h3>
              <p style={{ color: '#64748b' }}>
                The VIP certificate has been downloaded and saved.
              </p>
            </div>
          )}
        </div>

        <div className="modal-footer">
          {!generating && !success && (
            <>
              <button
                onClick={onClose}
                style={{
                  padding: '0.75rem 1.5rem',
                  background: '#e2e8f0',
                  color: '#475569',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontWeight: '600'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleGenerate}
                style={{
                  padding: '0.75rem 1.5rem',
                  background: 'linear-gradient(135deg, #14b8a6 0%, #0f766e 100%)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontWeight: '600'
                }}
              >
                Generate VIP Token
              </button>
            </>
          )}
          {success && (
            <button
              onClick={onClose}
              style={{
                padding: '0.75rem 1.5rem',
                background: '#16a34a',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                fontWeight: '600',
                width: '100%'
              }}
            >
              Close
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default TokenGeneratorModal;
